/**
 * Context Enhanced Component
 * Unique Properties: Variable scoping, state management, inheritance, configuration
 */

import { Alert, Badge, Card, Container, JsonInput, Paper, Stack, Tabs, Text, Title, Tree } from '@mantine/core';
import '@mantine/core/styles.css';
import { IconDatabase, IconSettings, IconVariable } from '@tabler/icons-react';
import React, { useEffect, useRef, useState } from 'react';
import { TabulatorFull } from 'tabulator-tables';
import 'tabulator-tables/dist/css/tabulator_midnight.min.css';
import WorkflowSearchMenu from './WorkflowSearchMenu';

interface ContextProps {
    data?: any;
    mode?: 'view' | 'edit' | 'diff';
}

export const ContextEnhanced: React.FC<ContextProps> = ({ data = {}, mode = 'view' }) => {
    const [activeTab, setActiveTab] = useState('variables');
    const [context, setContext] = useState(data.context || {});
    const tableRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (tableRef.current) {
            new TabulatorFull(tableRef.current, {
                data: [],
                layout: 'fitColumns',
                dataTree: true,
                columns: [
                    { title: 'Variable', field: 'name', editor: 'input' },
                    { title: 'Type', field: 'type' },
                    { title: 'Value', field: 'value', editor: 'input' },
                    { title: 'Scope', field: 'scope' }
                ]
            });
        }
    }, []);

    return (
        <Container size="xl">
            <Paper shadow="sm" p="lg">
                <Title order={2} mb="lg">Context Management</Title>
                <Tabs value={activeTab} onChange={(v) => setActiveTab(v ?? '')}>
                    <Tabs.List>
                        <Tabs.Tab value="variables" leftSection={<IconVariable size={16} />}>Variables</Tabs.Tab>
                        <Tabs.Tab value="scopes" leftSection={<IconDatabase size={16} />}>Scopes</Tabs.Tab>
                        <Tabs.Tab value="config" leftSection={<IconSettings size={16} />}>Configuration</Tabs.Tab>
                    </Tabs.List>
                    <Tabs.Panel value="variables">
                        <Stack mt="md">
                            <Alert color="blue">
                                Variables are scoped and inherited through the context hierarchy
                            </Alert>
                            <div ref={tableRef} />
                            <Card withBorder>
                                <Title order={5} mb="md">Context Tree</Title>
                                <Text size="sm" color="dimmed">
                                    Global → Tenant → Workflow → Instance → Local
                                </Text>
                            </Card>
                        </Stack>
                    </Tabs.Panel>
                    <Tabs.Panel value="config">
                        <Stack mt="md">
                            <JsonInput
                                label="Configuration"
                                minRows={15}
                                value={JSON.stringify(context, null, 2)}
                                onChange={(value) => setContext(JSON.parse(value))}
                            />
                        </Stack>
                    </Tabs.Panel>
                </Tabs>
            </Paper>
        </Container>
    );
};

export default ContextEnhanced;
